/**
 * Financial report permission keys (catalog `rpt.*`, docs/contracts/reports.md §5). Every report has its own view,
 * print (PDF), export (Excel) and send (schedule) key (RP6); the API checks the same keys.
 */
export const reportsPermissions = {
  balanceSheetView: "rpt.advance_balance.view",
  balanceSheetPrint: "rpt.advance_balance.print",
  balanceSheetExport: "rpt.advance_balance.export",
  balanceSheetSend: "rpt.advance_balance.send",
  incomeView: "rpt.advance_income.view",
  incomePrint: "rpt.advance_income.print",
  incomeExport: "rpt.advance_income.export",
  incomeSend: "rpt.advance_income.send",
  cashFlowView: "rpt.cash_flow_report.view",
  cashFlowPrint: "rpt.cash_flow_report.print",
  cashFlowExport: "rpt.cash_flow_report.export",
  cashFlowSend: "rpt.cash_flow_report.send",
  ratiosView: "rpt.ratio_analysis.view",
  ratiosPrint: "rpt.ratio_analysis.print",
  ratiosExport: "rpt.ratio_analysis.export",
  ratiosSend: "rpt.ratio_analysis.send",
  versionView: "rpt.statement_version.view",
  versionCreate: "rpt.statement_version.create",
  versionUpdate: "rpt.statement_version.update",
  versionDelete: "rpt.statement_version.delete",
  scheduleView: "rpt.report_schedule.view",
  scheduleCreate: "rpt.report_schedule.create",
  scheduleUpdate: "rpt.report_schedule.update",
  scheduleDelete: "rpt.report_schedule.delete",
  feedView: "rpt.balance_feed.view",
  archiveView: "rpt.issued_document.view",
} as const;

/** Plan module of the financial reports: they read the ledger, which every plan has (GL is always on). */
export const reportsModule = "gl";

/** Report codes of the API (`ReportCatalog`) and their route slug. */
export const reportCodes = ["balanceSheet", "incomeStatement", "comprehensiveIncome", "cashFlow", "ratios"] as const;
export type ReportCode = (typeof reportCodes)[number];

export const reportSlug: Record<ReportCode, string> = {
  balanceSheet: "balance-sheet",
  incomeStatement: "income-statement",
  comprehensiveIncome: "comprehensive-income",
  cashFlow: "cash-flow",
  ratios: "ratios",
};

/** The view / print / export / send keys of each report (RP6). */
export const reportKeys: Record<ReportCode, { view: string; print: string; export: string; send: string }> = {
  balanceSheet: {
    view: reportsPermissions.balanceSheetView,
    print: reportsPermissions.balanceSheetPrint,
    export: reportsPermissions.balanceSheetExport,
    send: reportsPermissions.balanceSheetSend,
  },
  incomeStatement: {
    view: reportsPermissions.incomeView,
    print: reportsPermissions.incomePrint,
    export: reportsPermissions.incomeExport,
    send: reportsPermissions.incomeSend,
  },
  comprehensiveIncome: {
    view: reportsPermissions.incomeView,
    print: reportsPermissions.incomePrint,
    export: reportsPermissions.incomeExport,
    send: reportsPermissions.incomeSend,
  },
  cashFlow: {
    view: reportsPermissions.cashFlowView,
    print: reportsPermissions.cashFlowPrint,
    export: reportsPermissions.cashFlowExport,
    send: reportsPermissions.cashFlowSend,
  },
  ratios: {
    view: reportsPermissions.ratiosView,
    print: reportsPermissions.ratiosPrint,
    export: reportsPermissions.ratiosExport,
    send: reportsPermissions.ratiosSend,
  },
};
